import { useCallback, useMemo, useState } from 'react';
import type React from 'react';
import App from 'antd/lib/app';
import { IUpdateCustomerUtilityBill } from 'src/domain/customer-utility-bill';
import {
    useReparseCustomerUtilityBillsMutation,
    useUnverifyCustomerUtilityBillsMutation,
    useUpdateCustomerUtilityBillMutation,
    useVerifyCustomerUtilityBillsMutation,
} from 'src/domain/customer-utility-bill/queries';
import { ICustomerUtilityBill } from 'src/domain/customer-utility-bill/interface';

const rowName = (row: ICustomerUtilityBill) => row.siteName || 'Bill';

// Pluralizes the bill count for batch notifications ("1 bill" / "3 bills").
const bills = (count: number) => `${count} ${count === 1 ? 'bill' : 'bills'}`;

// Text bill fields the operator may update; trimmed, and an emptied field goes as `null` so the backend clears it.
const TEXT_UPDATE_FIELDS = [
    'siteName',
    'siteNumber',
    'address',
    'city',
    'state',
    'zipCode',
    'accountNumber',
    'enrollmentId',
    'meterNumber',
    'rateClass',
    'controlProviderName',
    'utility',
    'market',
    'zone',
] as const;

// Map the edit form onto the backend update contract. Every updatable field is sent explicitly — an emptied
// field goes as `null` so the backend actually clears it (omitting it would leave the old value untouched).
// Fields locked by the source file are sent back unchanged. customerName / vendor aren't updatable. `verify`
// carries the operator's intent — the server stamps verifiedAt/verifiedBy.
const toUpdateBody = (form: ICustomerUtilityBill, verify: boolean): IUpdateCustomerUtilityBill => ({
    ...Object.fromEntries(TEXT_UPDATE_FIELDS.map(field => [field, form[field]?.trim() || null])),
    billingKw: form.billingKw ?? null,
    billingKwh: form.billingKwh ?? null,
    billingPeriodStart: form.billingPeriodStart || null,
    billingPeriodEnd: form.billingPeriodEnd || null,
    notes: form.notes || null,
    verify,
});

// Minimal shape of the verify/unverify/reparse mutations — enough to fire and await without importing their types.
type BatchResult = { skipped: { reason: string }[] };
type BatchMutation<R extends BatchResult> = {
    mutateAsync: (ids: string[]) => Promise<R>;
};

/**
 * Row + bulk actions for the bills list (verify / reparse / save) and the bulk selection state.
 *
 * Verify (single + bulk) hits POST /customer-utility-bills/verify; re-parse (single + bulk) hits
 * POST /customer-utility-bills/reparse; save hits PUT /customer-utility-bills/:id. The mutation hooks own
 * list invalidation + error notifications; success messaging is contextual and lives here.
 */
export const useBillActions = () => {
    const { notification } = App.useApp();
    // Track the full selected records (not just their keys) so selection-derived UI stays correct across
    // pages — the verify/unverify toggle needs each row's `verifiedAt`, including rows selected on other pages.
    const [selectedRows, setSelectedRows] = useState<ICustomerUtilityBill[]>([]);
    const selectedRowKeys = useMemo(() => selectedRows.map(row => row.id), [selectedRows]);

    // Merge the table's selection change into our record-keyed selection. antd hands us the records it knows
    // about for this change; for keys selected on other pages we fall back to the record we already held, so
    // every selected key keeps a full record (and de-selected keys drop out because we map over `keys`).
    const onSelectionChange = useCallback((keys: React.Key[], rows: ICustomerUtilityBill[]) => {
        setSelectedRows(prev => {
            const byId = new Map<React.Key, ICustomerUtilityBill>(prev.map(row => [row.id, row]));
            rows.forEach(row => row && byId.set(row.id, row));
            return keys.map(key => byId.get(key)).filter((row): row is ICustomerUtilityBill => !!row);
        });
    }, []);

    const updateMutation = useUpdateCustomerUtilityBillMutation();
    const verifyMutation = useVerifyCustomerUtilityBillsMutation();
    const unverifyMutation = useUnverifyCustomerUtilityBillsMutation();
    const reparseMutation = useReparseCustomerUtilityBillsMutation();

    // Single-row action: notify success when the row was affected, otherwise warn with the skip reason. Resolves
    // with whether the row was actually affected (e.g. for the edit modal to know whether it's safe to start
    // polling for a reparse outcome) — `false` on a skip or a failure (errors are already notified by the mutation).
    const runRowAction = useCallback(
        <R extends BatchResult>(
            row: ICustomerUtilityBill,
            mutation: BatchMutation<R>,
            getDoneIds: (res: R) => string[],
            messages: { done: (name: string) => string; fail: (name: string) => string },
            notifyType: 'success' | 'info'
        ): Promise<boolean> => {
            const name = rowName(row);
            return mutation
                .mutateAsync([row.id])
                .then(res => {
                    const done = getDoneIds(res).length > 0;
                    if (done) {
                        notification[notifyType]({ message: messages.done(name) });
                    } else {
                        const reason = res.skipped[0]?.reason;
                        notification.warning({ message: reason ? `${messages.fail(name)}: ${reason}` : messages.fail(name) });
                    }
                    return done;
                })
                .catch(() => false);
        },
        [notification]
    );

    // Fires the batch mutation, notifies done/skipped, clears the selection, and resolves with the accepted
    // ids (e.g. for reparse tracking) — [] on a no-op or failure (errors are already notified by the mutation).
    const runBulkAction = useCallback(
        <R extends BatchResult>(
            mutation: BatchMutation<R>,
            getDoneIds: (res: R) => string[],
            messages: { done: (count: number) => string; skip: (count: number) => string },
            notifyType: 'success' | 'info'
        ): Promise<string[]> => {
            if (!selectedRowKeys.length) return Promise.resolve([]);
            return mutation
                .mutateAsync(selectedRowKeys.map(String))
                .then(res => {
                    const doneIds = getDoneIds(res);
                    if (doneIds.length) notification[notifyType]({ message: messages.done(doneIds.length) });
                    if (res.skipped.length) notification.warning({ message: messages.skip(res.skipped.length) });
                    setSelectedRows([]);
                    return doneIds;
                })
                .catch(() => []);
        },
        [notification, selectedRowKeys]
    );

    const verify = useCallback(
        (row: ICustomerUtilityBill) =>
            runRowAction(
                row,
                verifyMutation,
                r => r.verifiedIds,
                { done: name => `Marked “${name}” as verified`, fail: name => `Couldn’t verify “${name}”` },
                'success'
            ),
        [runRowAction, verifyMutation]
    );

    const unverify = useCallback(
        (row: ICustomerUtilityBill) =>
            runRowAction(
                row,
                unverifyMutation,
                r => r.verifiedIds,
                { done: name => `Marked “${name}” as unverified`, fail: name => `Couldn’t unverify “${name}”` },
                'success'
            ),
        [runRowAction, unverifyMutation]
    );

    const reparse = useCallback(
        (row: ICustomerUtilityBill) =>
            runRowAction(
                row,
                reparseMutation,
                r => r.reparsedIds,
                { done: name => `Re-parse requested for “${name}”`, fail: name => `Couldn’t re-parse “${name}”` },
                'info'
            ),
        [runRowAction, reparseMutation]
    );

    const save = useCallback(
        (row: ICustomerUtilityBill, verified: boolean, onSuccess?: () => void) => {
            updateMutation.mutate(
                { id: row.id, body: toUpdateBody(row, verified) },
                {
                    onSuccess: () => {
                        notification.success({ message: 'Changes saved' });
                        onSuccess?.();
                    },
                }
            );
        },
        [notification, updateMutation]
    );

    const verifySelected = useCallback(
        () =>
            runBulkAction(
                verifyMutation,
                r => r.verifiedIds,
                { done: count => `Marked ${bills(count)} as verified`, skip: count => `Skipped ${bills(count)} that couldn’t be verified` },
                'success'
            ),
        [runBulkAction, verifyMutation]
    );

    const unverifySelected = useCallback(
        () =>
            runBulkAction(
                unverifyMutation,
                r => r.verifiedIds,
                { done: count => `Marked ${bills(count)} as unverified`, skip: count => `Skipped ${bills(count)} that couldn’t be unverified` },
                'success'
            ),
        [runBulkAction, unverifyMutation]
    );

    const reparseSelected = useCallback(
        () =>
            runBulkAction(
                reparseMutation,
                r => r.reparsedIds,
                { done: count => `Re-parse requested for ${bills(count)}`, skip: count => `Skipped ${bills(count)} that couldn’t be re-parsed` },
                'info'
            ),
        [runBulkAction, reparseMutation]
    );

    // Loading flags surfaced so the UI can show spinners: the edit modal on save, the bulk-action buttons
    // while their request is in flight. Verify/un-verify share a mutation instance with the row actions, so a
    // row-level (un)verify also lights up the matching bulk button — acceptable, both target the same endpoint.
    const isSaving = updateMutation.isLoading;
    const isVerifying = verifyMutation.isLoading;
    const isUnverifying = unverifyMutation.isLoading;
    const isReparsing = reparseMutation.isLoading;

    const clearSelection = useCallback(() => setSelectedRows([]), []);

    return {
        selectedRowKeys,
        selectedRows,
        onSelectionChange,
        clearSelection,
        verify,
        unverify,
        reparse,
        save,
        verifySelected,
        unverifySelected,
        reparseSelected,
        isSaving,
        isVerifying,
        isUnverifying,
        isReparsing,
    };
};
